import {
  MutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { APIKey, apiRoutes } from 'constants/apiKeys';
import baseFetcher from 'shared/helpers/baseFetcher';
import { AddShiftNoteData } from 'shared/hooks/api/shiftNotes';
import { Note } from 'shiftNotes/_types';
import { GuidanceQuestion } from 'priorities/_types';

export function useGuidanceQuestions() {
  return useQuery<GuidanceQuestion[]>({
    queryKey: [APIKey.GUIDANCE_QUESTIONS, 'list'],
    queryFn: () => baseFetcher(apiRoutes.guidanceQuestions()),
  });
}

type AnswerGuidanceQuestionVariables = {
  guidanceQuestionId: string;
  data: AddShiftNoteData;
};

type UseAnswerGuidanceQuestionMutationOptions = Pick<
  MutationOptions<Note, Error, AnswerGuidanceQuestionVariables>,
  'onSuccess'
>;

export function useAnswerGuidanceQuestionMutation({
  onSuccess,
}: UseAnswerGuidanceQuestionMutationOptions = {}) {
  const queryClient = useQueryClient();
  return useMutation<Note, Error, AnswerGuidanceQuestionVariables>({
    mutationFn: ({ guidanceQuestionId, data }) =>
      baseFetcher(apiRoutes.guidanceQuestionAnswer(guidanceQuestionId), {
        method: 'POST',
        data,
        removeHeaders: 'text_note' in data ? [] : ['Content-Type'],
      }),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [APIKey.GUIDANCE_QUESTIONS] });
      queryClient.invalidateQueries({ queryKey: [APIKey.USER] });
      queryClient.invalidateQueries({ queryKey: [APIKey.EVENT] });
      onSuccess?.(...args);
    },
  });
}
